import React, { useState, useContext, useMemo } from "react";
import "./styles/ContentInfo.scss";
import { Link } from "react-router-dom";
import ThesaurusIcon from "../../../../components/VectorIcons/ThesaurusIcon";
import CustomTooltip from "../../../../components/CustomTooltip/CustomTooltip";
import DateTooltip from "./DateTooltip/DateTooltip";
import EditIcon from "../../../../components/VectorIcons/EditIcon";
import CustomDatePicker from "../../../../components/CustomDatePicker/CustomDatepicker";
import AppContext from "../../../../context/appContext";
import _ from "lodash";
import ProspectIcon from "../../../../components/VectorIcons/ProspectIcon";
import FileView from "../../../../components/VectorIcons/FileView";
import AttachmentsContext from "../../../../context/attachmentsContext";
import useDownloadAttachment from "../../../../hooks/useDownloadAttachment";
import { VIEWABLE_FILE_TYPES } from "../../../../shared/constants/store";
import { getFileExtensionFromFileName } from "../../../../shared/utils/getFileExtensionFromFileName";
import { useAddendumAssociations } from "../../../../hooks/useAddendumAssociations";
import { handleDramaturgyTypeByFormat } from "../../../../shared/utils/handleDramaturgyTypeByFormat";

const isChapterFormat = (format) =>
  handleDramaturgyTypeByFormat(format) === "Dramaturgia Longa";

const formatContentInfoAddendumAssociations = (associations) =>
  associations.replace(/^CAP\./, "Cap").replace(/^EP\./, "Ep");

const AddendumFileName = ({ attachment, format }) => {
  const addendumId = attachment?.attachmentId;
  const isChapterAddendum = useMemo(() => isChapterFormat(format), [format]);

  const { addendumAssociations, isLoadingAddendumAssociations } =
    useAddendumAssociations({
      isAdendo: true,
      addendumId,
      isChapter: isChapterAddendum,
    });

  if (isLoadingAddendumAssociations) {
    return <span className="addendumAssociationsSkeleton" />;
  }

  return addendumAssociations
    ? formatContentInfoAddendumAssociations(addendumAssociations)
    : attachment.attachmentType?.name;
};

const ContentInfo = (props) => {
  const {
    projectName,
    creators,
    suggestedBy,
    format,
    genres,
    attachments,
    date,
    getDate,
    type,
    projectId,
    isEditable,
  } = props;

  const appContext = useContext(AppContext);

  const { setFileToView } = useContext(AttachmentsContext);
  const { downloadAttachment } = useDownloadAttachment();

  const [stateEditDate, setStateEditDate] = useState(true);

  const compileFiles = (data) => {
    let allAgroup = compileAll(data);
    let materialCompiled = compileMaterial(data);
    let chapterCompiled = compiledChapter(data);

    let mergeGroups = { ...materialCompiled, ...allAgroup, ...chapterCompiled };
    let merge = Object.entries(mergeGroups).map((files) => files[1]);

    return merge;
  };

  const compiledChapter = (data) => {
    let chapter = _.cloneDeep(data);
    let chapterFilter = chapter.filter(
      (e) =>
        e.attachmentType?.name === "Capítulo/Episódio" ||
        e.attachmentType?.name === "Episódio"
    );

    let compiled = _.groupBy(
      chapterFilter,
      (u) => u.blockNumber && u.chapterNumber
    );

    return compiled;
  };

  const compileAll = (data) => {
    let groupall = _.cloneDeep(data);
    return _.groupBy(
      groupall.filter(
        (e) =>
          e.attachmentType?.name !== "Material Complementar" &&
          e.attachmentType?.name !== "Capítulo/Episódio" &&
          e.attachmentType?.name !== "Episódio"
      ),
      (u) => u.attachmentType?.id
    );
  };

  const compileMaterial = (data) => {
    let emptyArray = [];
    let compiledMaterial = _.cloneDeep(data);
    let material = compiledMaterial.filter(
      (e) => e.attachmentType?.name === "Material Complementar"
    );
    compiledMaterial.filter(
      (e) => e.attachmentType?.name === "Material Complementar"
    );
    material.map((e) => emptyArray.push([e]));

    return emptyArray;
  };

  const checkVersion = (version) =>
    version > 1 ? `<span>(${version}ª versão)</span>` : "";

  const returnTypeAttachment = (attachment) => {
    let files = {
      "Capítulo/Episódio": `<div class="typeBloco">bloco ${attachment.blockNumber}</div>`,
      Episódio: `<div class="typeBloco">bloco ${attachment.blockNumber}</div>`,
      default: "",
    };
    return files[attachment.attachmentType?.name] || files["defaut"];
  };

  const creatorsOrSuggesters = {
    dramaturgia: creators,
    variedades: creators,
    "obra-literaria": suggestedBy,
  };

  return (
    <div className="contentInfo">
      <div className="infoLine">
        <span className="infoName">
          {type === "obra-literaria" ? "Obra Literária" : "Projeto"}
        </span>

        <Link to={appContext.redirectToSheet(type, projectId)}>
          {projectName}
        </Link>
      </div>

      <div className="infoLine">
        <span className="infoName">
          {type === "obra-literaria" ? "Solicitado por" : "Criado por"}
        </span>
        <div className="infoData creators">
          {creatorsOrSuggesters[type].map((creator, index) => {
            return (
              <div key={index}>
                <span className="creatorName">
                  {creator.origin === 1 && (
                    <ProspectIcon color="#B2B4F1" width={18} height={18} />
                  )}
                  {creator.name}
                </span>
                {index + 1 < creatorsOrSuggesters[type].length && (
                  <span className="infoSeparator">|</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="infoLine">
        <span className="infoName">Formato</span>
        <div className="infoData">{format}</div>
      </div>

      <div className="infoLine">
        <span className="infoName">Gênero</span>
        <div className="infoData">
          {!!genres.length ? (
            genres.map((genre, index) => {
              return (
                <div key={index}>
                  <span className="creatorName">{genre}</span>
                  {index + 1 < genres.length && (
                    <span className="infoSeparator">|</span>
                  )}
                </div>
              );
            })
          ) : (
            <span>-</span>
          )}
        </div>
      </div>

      {!!attachments.length && (
        <div className="infoLine">
          <span className="infoName">Anexos</span>
          <div className="infoData">
            {compileFiles(attachments).map((files, index) => {
              return (
                <div className="contentListFiles" key={index}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: returnTypeAttachment(files[0]),
                    }}
                  />
                  {_.orderBy(files, ["version"], ["asc"]).map(
                    (attachment, i) => {
                      const fileToVisualize = {
                        fileData: {
                          ...attachment,
                          id: attachment.attachmentId,
                        },
                        contentId: projectId,
                        origin: "visualizador-parecer-atividade",
                        mode: 1,
                        autoSave: false,
                      };

                      const fileToDownload = {
                        fileData: {
                          ...attachment,
                          id: attachment.attachmentId,
                        },
                        contentId: projectId,
                        origin: "parecer-atividade",
                        mode: 0,
                      };

                      const fileExtension = getFileExtensionFromFileName(
                        attachment?.fileName
                      );

                      const canViewFile =
                        VIEWABLE_FILE_TYPES.includes(fileExtension) &&
                        attachment?.fileName !== null;
                      const isAddendum =
                        attachment.attachmentType?.name === "Adendo";

                      return (
                        <div className="childrenAttachment" key={i}>
                          {isAddendum && (
                            <span className="attachmentTypeName">
                              {attachment.attachmentType?.name}
                            </span>
                          )}

                          <div
                            className="fileName"
                            onClick={
                              canViewFile
                                ? () => setFileToView(fileToVisualize)
                                : () => {}
                            }
                            style={{
                              cursor: canViewFile ? "pointer" : "default",
                            }}
                          >
                            {canViewFile && (
                              <FileView
                                color={canViewFile ? "#0098CD" : "#CCC"}
                              />
                            )}

                            {!canViewFile && (
                              <CustomTooltip
                                tipLabel={
                                  <FileView
                                    color={canViewFile ? "#0098CD" : "#CCC"}
                                  />
                                }
                                dataTip="Formato de arquivo não suportado para visualização"
                                position="left"
                                theme="light"
                              />
                            )}
                          </div>

                          <span
                            className="fileName"
                            onClick={() => downloadAttachment(fileToDownload)}
                          >
                            {isAddendum ? (
                              <AddendumFileName
                                attachment={attachment}
                                format={format}
                              />
                            ) : attachment.attachmentType?.name ===
                                "Capítulo/Episódio" ||
                              attachment.attachmentType?.name === "Episódio" ? (
                              <div>
                                Cap {attachment.chapterNumber}
                                {attachment.version > 1 && (
                                  <span
                                    className="fileVersion"
                                    dangerouslySetInnerHTML={{
                                      __html: checkVersion(attachment.version),
                                    }}
                                  />
                                )}
                              </div>
                            ) : (
                              <div>
                                {attachment.attachmentType?.name}
                                {!!attachment.version > 1 && (
                                  <span
                                    className="fileVersion"
                                    dangerouslySetInnerHTML={{
                                      __html: checkVersion(attachment.version),
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </span>
                          {i + 1 < files.length && (
                            <span className="infoSeparator"> | </span>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="infoLine">
        <span className="infoName">
          Data
          <CustomTooltip
            tipLabel={<ThesaurusIcon handleClick={() => {}} />}
            dataTip={<DateTooltip />}
          />
        </span>

        <div className="infoData">
          {stateEditDate ? (
            <>
              {appContext.formatDate(appContext.convertDatePtBr(date))}
              {` às`}
              {appContext.convertDateAndTimePtBr(date).slice(10, 17)}

              {isEditable && (
                <div
                  className="editDate"
                  onClick={() => setStateEditDate(false)}
                >
                  <EditIcon size={14} />
                </div>
              )}
            </>
          ) : (
            <div className="datepickerContainer">
              <CustomDatePicker
                darkBackgroundColor={true}
                date={new Date(date)}
                handleChangeDate={getDate}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentInfo;
